import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import api from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-toastify';
import { format, parseISO, addDays } from 'date-fns';
import { FiClock, FiCalendar, FiDollarSign, FiCheck } from 'react-icons/fi';

const Booking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability();
    }
  }, [selectedDate]);

  useEffect(() => {
    const courseId = searchParams.get('courseId');
    if (courseId && courses.length > 0) {
      const course = courses.find(c => c.id === courseId);
      if (course) setSelectedCourse(course);
    }
  }, [searchParams, courses]);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const startDate = format(selectedDate, 'yyyy-MM-dd');
      const endDate = format(addDays(selectedDate, 7), 'yyyy-MM-dd');
      const response = await api.get('/availability', {
        params: { startDate, endDate }
      });
      setAvailability(response.data);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  const getAvailableSlotsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.filter(slot => slot.date === dateStr);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const tileClassName = ({ date }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const hasSlots = availability.some(slot => slot.date === dateStr);
    return hasSlots ? 'react-calendar__tile--available' : '';
  };

  const handleBooking = async () => {
    if (!selectedCourse || !selectedSlot) {
      toast.error('Please select a course and time slot');
      return;
    }

    setBookingLoading(true);
    try {
      const bookingData = {
        courseId: selectedCourse.id,
        availabilityId: selectedSlot.id,
        bookingDate: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        notes
      };

      const response = await api.post('/bookings', bookingData);
      const booking = response.data.booking;

      toast.success('Booking created! Proceeding to payment...');
      
      // Create PayPal payment
      const paymentResponse = await api.post('/payments/create', {
        bookingId: booking.id
      });

      // Redirect to PayPal
      window.location.href = paymentResponse.data.approvalUrl;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Booking failed');
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;

  const availableSlotsForDate = getAvailableSlotsForDate(selectedDate);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book Your Session
          </h1>
          <p className="text-xl text-gray-600">
            Choose your course, select a time, and secure your spot
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Course & Calendar */}
          <div className="lg:col-span-2 space-y-6">
            {/* Course Selection */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FiCheck className="mr-2 text-primary-500" />
                Step 1: Select Course
              </h2>
              <select
                value={selectedCourse?.id || ''}
                onChange={(e) => {
                  const course = courses.find(c => c.id === e.target.value);
                  setSelectedCourse(course);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
              >
                <option value="">Choose a course...</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.title} - ${course.price} ({course.duration} min)
                  </option>
                ))}
              </select>

              {selectedCourse && (
                <div className="mt-4 p-4 bg-primary-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">{selectedCourse.title}</h3>
                  <p className="text-gray-700 text-sm mb-3">{selectedCourse.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center text-gray-700">
                      <FiClock className="mr-1 text-primary-500" />
                      {selectedCourse.duration} minutes
                    </span>
                    <span className="flex items-center text-gray-700">
                      <FiDollarSign className="mr-1 text-primary-500" />
                      ${selectedCourse.price}
                    </span>
                    <span className="px-3 py-1 bg-primary-200 text-primary-800 rounded-full font-medium">
                      {selectedCourse.level}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Calendar */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FiCalendar className="mr-2 text-primary-500" />
                Step 2: Select Date
              </h2>
              <Calendar
                onChange={handleDateChange}
                value={selectedDate}
                minDate={new Date()}
                tileClassName={tileClassName}
                className="mx-auto"
              />
              <div className="mt-4 flex items-center justify-center space-x-4 text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-200 rounded mr-2"></div>
                  <span className="text-gray-600">Available</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-yellow-200 rounded mr-2"></div>
                  <span className="text-gray-600">Today</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-primary-500 rounded mr-2"></div>
                  <span className="text-gray-600">Selected</span>
                </div>
              </div>
            </div>

            {/* Time Slots */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FiClock className="mr-2 text-primary-500" />
                Step 3: Select Time
              </h2>
              <p className="text-gray-600 mb-4">
                Available slots for {format(selectedDate, 'MMMM d, yyyy')}
              </p>
              
              {availableSlotsForDate.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No available slots for this date. Please select another date.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableSlotsForDate.map(slot => (
                    <button
                      key={slot.id}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-4 rounded-lg border-2 transition ${
                        selectedSlot?.id === slot.id
                          ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                      }`}
                    >
                      {slot.startTime.slice(0, 5)} - {slot.endTime.slice(0, 5)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Additional Notes (Optional)
              </h2>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Any specific topics you'd like to focus on?"
              />
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Booking Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Course</p>
                  <p className="font-semibold text-gray-900">
                    {selectedCourse ? selectedCourse.title : 'Not selected'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Date</p>
                  <p className="font-semibold text-gray-900">
                    {format(selectedDate, 'MMMM d, yyyy')}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Time</p>
                  <p className="font-semibold text-gray-900">
                    {selectedSlot 
                      ? `${selectedSlot.startTime.slice(0, 5)} - ${selectedSlot.endTime.slice(0, 5)}`
                      : 'Not selected'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Duration</p>
                  <p className="font-semibold text-gray-900">
                    {selectedCourse ? `${selectedCourse.duration} minutes` : '-'}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-primary-600">
                      ${selectedCourse ? selectedCourse.price : '0.00'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleBooking}
                disabled={!selectedCourse || !selectedSlot || bookingLoading}
                className="w-full bg-gradient-to-r from-primary-500 to-secondary-500 text-white py-4 rounded-lg font-semibold hover:shadow-lg transition transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {bookingLoading ? 'Processing...' : 'Proceed to Payment'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                You'll be redirected to PayPal for secure payment
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;

